/**
 * <WeekSlotChip> — un horaire de la semaine, une case qu'on coche (molecule).
 *
 * La case d'une grille où le serveur a déjà dit quelque chose de chaque horaire : un horaire
 * ordinaire (la face de la carte, le contour du livre), le même horaire marqué d'une pastille de
 * couleur quand il laisse un peu plus de chances de garder le même tuteur (`tones`, une pastille par
 * tuteur, sans visage : c'est une chance, pas une promesse), ou un cours que l'enfant garde (l'aplat
 * de marque, foncé dans les deux thèmes, et une petite mention — jamais cochable), ou le cours qui
 * change (`changing` : la face claire cerclée de la marque, l'heure barrée et sa mention — l'heure
 * que la famille quitte se voit sur la grille où elle choisit la suivante). Plus haute
 * qu'une pilule (44 px, la cible du doigt), parce qu'une grille entière se coche au pouce.
 *
 * Cochée, la sélection se lit au CONTOUR, plus épais, dans la couleur de l'univers, et à la pastille
 * de coche posée sur son coin haut droit, hors de la case pour ne rien cacher de l'heure. L'épaisseur
 * du contour change, pas la taille de la case : le rembourrage rend ce que le contour prend, et
 * cocher ne décale rien.
 *
 * `swatch` : la face seule, petite, sans libellé ni geste — la pastille de la légende sous la
 * grille, pour que la légende montre exactement ce qu'elle explique (barrée d'un trait pour le
 * cours qui change).
 *
 * Contrôlé : `selected` arrive, `onPress` repart. Aucune copie : l'heure et la mention arrivent
 * en props.
 */
import React from "react"
import { Pressable, View } from "react-native"
import { StyleSheet, useUnistyles } from "react-native-unistyles"

import { inkOnFill, type SemanticColorKey } from "../../theme/tokens/colors"
import { FocusRing, focusRingHost } from "../../atoms/FocusRing"
import { IconBubble } from "../../atoms/icons/IconBubble"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Text } from "../../atoms/typography/Text"

/** `<Pressable>` ne type que `pressed` ; react-native-web ajoute `focused` à l'exécution. */
type PressableVisualState = { pressed: boolean; focused?: boolean }

/** La hauteur minimale d'une case : la cible du doigt du livre. */
export const WEEK_SLOT_CHIP_HEIGHT = 44
/** La pastille d'un tuteur, sur une case et dans la légende. */
const DOT_SIZE = 10
/** La pastille de légende. */
const SWATCH_SIZE = 16
/** Le contour d'une case, et celui d'une case cochée : plus épais, pour se lire au premier coup d'œil. */
const BORDER = 1.5
const BORDER_SELECTED = 2.5
/** La pastille de coche, et de combien elle déborde du coin de la case. */
const CHECK_SIZE = 18
const CHECK_OFFSET = -7

/** Le trait qui barre la pastille de légende du cours qui change. */
const STRIKE = 1.5

/** L'aplat d'un cours gardé : la marque, foncée sur le clair comme sur la nuit. */
const COURSE_FILL: SemanticColorKey = "brand-fill"
const COURSE_INK: SemanticColorKey = "white"

export type WeekSlotChipProps = {
    /** L'heure — « 17h00 ». */
    label: string
    selected?: boolean
    onPress?: () => void
    /**
     * Les couleurs des tuteurs que cet horaire laisse un peu plus de chances de garder : une pastille
     * chacune, deux au plus. Vide, la case ordinaire.
     */
    tones?: SemanticColorKey[]
    /** Un cours que l'enfant garde : foncé, jamais cochable. */
    course?: boolean
    /** Le cours qui change : clair, cerclé de la marque, l'heure barrée — jamais cochable. */
    changing?: boolean
    /** La mention sous l'heure d'un cours gardé (« cours ») ou du cours qui change (« à changer »). */
    caption?: string
    /** La pastille de légende : la face seule, sans libellé ni geste. */
    swatch?: boolean
    accessibilityLabel?: string
    testID?: string
}

/** Le fond, l'encre et le contour d'une case : un cours gardé, le cours qui change, ou la case ordinaire. */
function colorsOf(
    course: boolean,
    changing: boolean
): { fill: SemanticColorKey; ink: SemanticColorKey; edge: SemanticColorKey } {
    if (changing) return { fill: "surface-raised", ink: "text-strong", edge: COURSE_FILL }
    if (course) return { fill: COURSE_FILL, ink: COURSE_INK, edge: COURSE_FILL }
    return { fill: "surface-raised", ink: "text-primary", edge: "border" }
}

export function WeekSlotChip(props: WeekSlotChipProps) {
    const {
        label,
        selected = false,
        onPress,
        tones = [],
        course = false,
        changing = false,
        caption,
        swatch = false,
        accessibilityLabel,
        testID,
    } = props
    const { theme } = useUnistyles()
    const { fill, ink, edge } = colorsOf(course, changing)
    const dots = course || changing ? [] : tones.slice(0, 2)

    if (swatch) {
        if (dots[0] != null) return <View testID={testID} style={styles.dot(dots[0])} />
        return (
            <View testID={testID} style={styles.swatch(fill, edge, !course && !changing)}>
                {changing && <View style={styles.swatchStrike(edge)} />}
            </View>
        )
    }

    const disabled = course || changing || onPress == null
    return (
        <Pressable
            testID={testID}
            onPress={onPress}
            disabled={disabled}
            accessibilityRole="checkbox"
            accessibilityLabel={accessibilityLabel ?? label}
            accessibilityState={{ checked: selected, disabled }}
            style={({ pressed }: PressableVisualState) => [
                focusRingHost,
                styles.chip(fill, edge, selected),
                pressed && !disabled ? styles.pressed : undefined,
            ]}
        >
            {({ focused }: PressableVisualState) => (
                <>
                    <FocusRing visible={focused === true && !disabled} radius={theme.radius.md} />
                    <Stack gap="0" align="center">
                        <Row gap="1" align="center">
                            {dots.map((tone, index) => (
                                <View key={index} style={styles.dot(tone)} />
                            ))}
                            <Text
                                size="text-sm"
                                weight="bold"
                                color={ink}
                                numberOfLines={1}
                                style={changing ? styles.struck : undefined}
                            >
                                {label}
                            </Text>
                        </Row>
                        {(course || changing) && caption != null && caption !== "" && (
                            <Text size="text-xxs" weight="semibold" color={ink}>
                                {caption}
                            </Text>
                        )}
                    </Stack>
                    {selected && (
                        <View style={styles.check} pointerEvents="none">
                            <IconBubble iconName="check" size={CHECK_SIZE} color="role" iconColor={inkOnFill("role")} />
                        </View>
                    )}
                </>
            )}
        </Pressable>
    )
}

const styles = StyleSheet.create((theme) => ({
    chip: (fill: SemanticColorKey, edge: SemanticColorKey, selected: boolean) => {
        const border = selected ? BORDER_SELECTED : BORDER
        return {
            minHeight: WEEK_SLOT_CHIP_HEIGHT,
            minWidth: 64,
            // Le rembourrage rend au contour ce qu'il prend : la case garde sa taille cochée ou non.
            paddingHorizontal: theme.spacing["3"] + BORDER_SELECTED - border,
            paddingVertical: theme.spacing["1"] + BORDER_SELECTED - border,
            alignItems: "center" as const,
            justifyContent: "center" as const,
            borderRadius: theme.radius.md,
            borderWidth: border,
            backgroundColor: theme.colors[fill],
            // Cochée : le contour de l'univers. Sinon : celui du livre sur la face ordinaire, l'aplat
            // lui-même sur un cours gardé.
            borderColor: selected ? theme.colors.role : theme.colors[edge],
        }
    },
    // La pastille de la case ordinaire (« Autre horaire ») se dessine en pointillé, comme la maquette.
    swatch: (fill: SemanticColorKey, edge: SemanticColorKey, dashed: boolean) => ({
        width: SWATCH_SIZE,
        height: SWATCH_SIZE * 0.75,
        borderRadius: theme.radius.sm,
        borderWidth: BORDER,
        borderStyle: dashed ? ("dashed" as const) : ("solid" as const),
        borderColor: theme.colors[edge],
        backgroundColor: theme.colors[fill],
        justifyContent: "center" as const,
    }),
    // Le trait du cours qui change, en travers de sa pastille, comme sur l'heure de sa case.
    swatchStrike: (edge: SemanticColorKey) => ({ height: STRIKE, backgroundColor: theme.colors[edge] }),
    struck: { textDecorationLine: "line-through" as const },
    dot: (tone: SemanticColorKey) => ({
        width: DOT_SIZE,
        height: DOT_SIZE,
        borderRadius: DOT_SIZE / 2,
        backgroundColor: theme.colors[tone],
    }),
    check: { position: "absolute" as const, top: CHECK_OFFSET, right: CHECK_OFFSET },
    pressed: { opacity: 0.8 },
}))
